import React from "react";
import { useArena } from "core/components/ArenaProvider";
import NoMatchScreen from "arena/components/NoMatchScreen";
import { useLocation } from "react-router-dom";
import PageHelmet from "./PageHelmet";
import PageSection from "./PageSection";
import PageHeading from "./PageHeading";
import usePageview from "core/hooks/usePageview";
import LoadingScreen from "core/components/LoadingScreen";
import ErrorScreen from "core/components/ErrorScreen";
import { useQuery } from "@apollo/client";
import { ArenaPageQuery } from "arena/queries";
import ProxyPage from "arena/components/ProxyPage/ProxyPage";
import { isChannelProxyPage } from "arena/components/ProxyPage/proxyPageOverride";
import ArenaRedirect from "arena/components/ArenaRedirect";

const COVER = "COVER";
const COVER_CAROUSEL = "COVER_CAROUSEL";

/**
 * Тот же путь со слэшем в конце или без него. Для главной вариантов нет.
 */
export function toggleTrailingSlash(pathname) {
  if (!pathname || pathname === "/") return null;

  return pathname.endsWith("/") ? pathname.slice(0, -1) : `${pathname}/`;
}

/**
 * Функция принимает массив блоков для страницы и возвращает массив блоков
 * в котором блоки типа "Обложка" сгруппированы в карусельку.
 */
function groupBlocks(blocks) {
  const result = [];

  blocks.forEach((block) => {
    const last = result[result.length - 1];

    if (block.component === COVER) {
      if (last?.component === COVER_CAROUSEL) {
        last.props.blocks.push(block);
      } else {
        result.push({
          id: block.id,
          component: COVER_CAROUSEL,
          props: {
            blocks: [block],
          },
        });
      }
    } else {
      result.push(block);
    }
  });

  return result;
}

export default function Page() {
  const arena = useArena();
  const location = useLocation();

  const { data, previousData, loading, error } = useQuery(ArenaPageQuery, {
    fetchPolicy: "cache-and-network",
    variables: {
      path: location.pathname,
    },
  });

  const page = data !== undefined ? data?.page : previousData?.page;
  const arenaProxyPage =
    data !== undefined ? data?.arenaProxyPage : previousData?.arenaProxyPage;

  /**
   * Страницы заводят в админке с точным путём: одни со слэшем в конце,
   * другие без. Если по адресу ничего нет, ищем второй вариант и уводим
   * на него постоянным редиректом, чтобы у страницы был один адрес.
   */
  const notFound = data !== undefined && !page && !arenaProxyPage;
  const alternatePath = toggleTrailingSlash(location.pathname);
  const alternate = useQuery(ArenaPageQuery, {
    skip: !notFound || !alternatePath,
    variables: {
      path: alternatePath,
    },
  });

  usePageview(location.pathname, arenaProxyPage?.title || page?.title);

  if (loading && data === undefined) return <LoadingScreen />;

  if (error) return <ErrorScreen error={error} />;

  if (!page && !arenaProxyPage) {
    if (alternate.data?.page || alternate.data?.arenaProxyPage) {
      return (
        <ArenaRedirect url={`${alternatePath}${location.search}`} permanent />
      );
    }

    if (alternate.loading) return <LoadingScreen />;

    return <NoMatchScreen />;
  }

  /**
   * Прокси-страница и обычная страница ищутся по одному и тому же пути, и
   * теоретически на нём могут найтись обе. Прокси-страница главнее: её
   * адрес заведён под конкретный контент, а блоки страницы иначе встали бы
   * вторым слоем под ним.
   */
  if (arenaProxyPage) {
    // Канал показывает AppRoutes тем же диалогом, что и /tv/:slug
    // (см. useProxyChannel), под диалогом страница пустая
    if (isChannelProxyPage(arenaProxyPage)) {
      return null;
    }

    return <ProxyPage arenaProxyPage={arenaProxyPage} />;
  }

  const blocks = page ? groupBlocks(page.blocks) : [];

  return (
    <>
      {page && (
        <>
          <PageHelmet arena={arena} page={page} />
          <PageHeading title={page.title} />
          {blocks.map((block, index) => (
            <PageSection
              block={block}
              arena={arena}
              index={index}
              first={index === 0}
              last={index + 1 === blocks.length}
              key={block.id}
            />
          ))}
        </>
      )}
    </>
  );
}
